import { FormGroup } from '@angular/forms';
import {
  AccessoriesCartItem,
  CartDto,
  ProductItemType,
  ShippingCartItem,
  SoilAnalysisCartItem,
  SoilAnalysisGroundSnapshotDto
} from '@as/api';
import { SoilAnalysisBundleOptions } from '../models/soil-analysis-budle-options.model';
import { SoilAnalysisOrderFormModel, SoilAnalysisOrderFormValueModel } from '../models/soil-analysis-order-form.model';

export interface CatalogCartProduct {
  productCode: string;
}

export function createEmptyCart(): CartDto {
  return {
    version: 2,
    updatedAt: new Date().toISOString(),
    items: []
  };
}

export function buildSoilAnalysisCartItem(
  form: FormGroup<SoilAnalysisOrderFormModel>,
  bundles: SoilAnalysisBundleOptions[]
): SoilAnalysisCartItem {
  const selected = form.controls.selectedGrounds.value ?? [];
  const includeBags = form.controls.includeBags.value ?? false;

  const selectedGrounds: SoilAnalysisGroundSnapshotDto[] = selected.map(g => ({
    id: g.id,
    name: g.name,
    areaInHa: g.areaInHa ?? 0,
    locations: g.locations ?? []
  }));

  const summarizedAreaInHa = round2(selectedGrounds.reduce((acc, g) => acc + g.areaInHa, 0));

  const bundleForms = form.controls.bundles.controls;
  const pickedForm = bundleForms.find(fg => (fg.controls.count.value ?? 0) > 0);
  if (!pickedForm) {
    throw new Error('No bundle selected');
  }

  const productCode = pickedForm.controls.productCode.value;
  const quantity = pickedForm.controls.count.value ?? 0;

  const bundleConfig = bundles.find(b => b.productCode === productCode);
  if (!bundleConfig) {
    throw new Error(`Missing bundle config for tier ${productCode}`);
  }

  return {
    id: crypto.randomUUID(),
    type: ProductItemType.SoilAnalysis,
    createdAt: new Date().toISOString(),
    details: {
      provider: (bundleConfig.provider || 'FARMTECH') as 'FARMTECH',
      selectedGrounds,
      summarizedAreaInHa,
      includeBags,
      bundle: {
        productCode,
        quantity
      }
    }
  };
}

export function buildAccessoriesCartItem(parentId: string, quantity: number, product: CatalogCartProduct): AccessoriesCartItem {
  return {
    id: crypto.randomUUID(),
    type: ProductItemType.Accessories,
    createdAt: new Date().toISOString(),
    details: {
      productCode: product.productCode,
      quantity,
      parentCartItemId: parentId
    }
  };
}

export function buildBagsCartItem(parentId: string, quantity: number, product: CatalogCartProduct): AccessoriesCartItem {
  return buildAccessoriesCartItem(parentId, quantity, product);
}

export function buildShippingCartItem(product: CatalogCartProduct): ShippingCartItem {
  return {
    id: crypto.randomUUID(),
    type: ProductItemType.Shipping,
    createdAt: new Date().toISOString(),
    details: {
      productCode: product.productCode,
      quantity: 1
    }
  };
}

export function soilAnalysisCartItemToInitialValue(item: SoilAnalysisCartItem): SoilAnalysisOrderFormValueModel {
  return {
    selectedGrounds: item.details.selectedGrounds.map(g => ({
      id: g.id,
      name: g.name,
      areaInHa: g.areaInHa,
      locations: g.locations
    })),
    bundles: [
      {
        productId: item.details.bundle.productCode,
        productName: '',
        unitPriceNet: 0,
        count: item.details.bundle.quantity
      }
    ],
    includeBags: item.details.includeBags
  };
}

function round2(v: number): number {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}
